import { appToast } from '@/shared/components/toast/toast'
import { STATUS_MESSAGES } from './apiStatusMessages'
import { unixTimeStampNow } from './datetime'

import type { ResponseWithMethod } from '@/shared/composables/useApi'

const CONTACT_SUPPORT = 'Please contact support if the problem persists.'
const DEFAULT_TITLE = 'Unexpected error.'

/** Header per HTTP method, kept deliberately non-technical. */
const METHOD_TITLES: Record<string, string> = {
  GET: 'Error while trying to load data.',
  POST: 'Error while trying to save.',
  PUT: 'Error while trying to update.',
  PATCH: 'Error while trying to update.',
  DELETE: 'Error while trying to delete.'
}

/**
 * Status-specific messages that take precedence over the method message.
 * Auth failures shouldn't tell the user to "contact support".
 */
const STATUS_OVERRIDES: Record<number, { summary: string; detail: string }> = {
  401: { summary: 'Your session is invalid', detail: 'Please sign in again or refresh the page to continue.' },
  403: { summary: 'Access denied', detail: 'You do not have permission to perform this action.' }
}

/** Suppress an identical error toast (same header + status) for this many seconds. */
const DUPLICATE_WINDOW_SECONDS = 5
const lastShownAt = new Map<string, number>()

/**
 * Central API error handler. Shows a toast whose header is a friendly,
 * method-specific message (load/save/update/delete), with the contact line and
 * a status reference underneath. Returns the header so callers can surface it
 * inline.
 *
 * Reporting to Sentry is handled upstream at the useApi chokepoint (every
 * failed request is reported there), so this handler is purely presentational
 * and intentionally does not log — that would double-report via
 * captureConsoleIntegration.
 */
export const handleApiError = (error: unknown): string => {
  if (error instanceof DOMException && error.name === 'AbortError') return ''

  let status: number | undefined
  let method: string | undefined
  let serverDate: string | null = null

  if (error instanceof Response) {
    const taggedResponse = error as ResponseWithMethod
    status = taggedResponse.status
    method = taggedResponse.method
    serverDate = taggedResponse.headers.get('date')
  } else if (error && typeof error === 'object') {
    status = (error as { statusCode?: number }).statusCode
  }

  const override = status ? STATUS_OVERRIDES[status] : undefined
  const mapped = status ? STATUS_MESSAGES[status] : undefined
  const summary = override?.summary ?? ((method && METHOD_TITLES[method]) || DEFAULT_TITLE)
  const detail = override?.detail ?? CONTACT_SUPPORT
  const reference = status ? `Error ${status}${mapped ? ` - ${mapped.title}` : ''}` : undefined
  // Prefer the server's Date header (better for log correlation); fall back to
  // client time when it's absent (network errors, non-exposed CORS) or unparseable.
  const parsedServerDate = serverDate ? Date.parse(serverDate) : NaN
  const timestamp = Number.isNaN(parsedServerDate) ? unixTimeStampNow() : Math.floor(parsedServerDate / 1000)

  const dedupeKey = `${summary}|${reference ?? ''}`
  const previousAt = lastShownAt.get(dedupeKey)
  if (previousAt !== undefined && timestamp - previousAt < DUPLICATE_WINDOW_SECONDS) {
    return summary
  }
  lastShownAt.set(dedupeKey, timestamp)

  // Unix timestamp shown raw on purpose: it's a locale-independent reference
  // for correlating user reports with server logs
  appToast.error(summary, {
    detail,
    footerLeft: reference,
    footerRight: String(timestamp)
  })

  return summary
}
